import { useEffect, useState } from 'react'
import { listSignInNames, signIn, type SignInName } from '../lib/api'

interface Props {
  onSignedIn: (token: string) => void
}

/**
 * Name-picker sign-in.
 *
 * Staff tap their name and are in. Admins additionally enter a PIN, because an
 * admin can create, reassign and see everyone's work, whereas a staff action is
 * low-stakes and reversible.
 *
 * This exists so a second device is not a chore: hunting down a long personal
 * link on a laptop is the kind of friction that stops a tool being used.
 * Personal links still work exactly as before — this is an additional door to
 * the same room, and it hands back the identical token.
 */
export default function SignIn({ onSignedIn }: Props) {
  const [names, setNames] = useState<SignInName[] | null>(null)
  const [chosen, setChosen] = useState<SignInName | null>(null)
  const [pin, setPin] = useState('')
  const [problem, setProblem] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let cancelled = false
    listSignInNames()
      .then((rows) => {
        if (!cancelled) setNames(rows)
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setNames([])
          setProblem(err instanceof Error ? err.message : String(err))
        }
      })
    return () => {
      cancelled = true
    }
  }, [])

  const attempt = async (person: SignInName, withPin?: string) => {
    setBusy(true)
    setProblem(null)
    try {
      const result = await signIn(person.employee_id, withPin)
      if (result.ok && result.token) {
        onSignedIn(result.token)
      } else {
        setProblem(result.message ?? 'Could not sign in.')
        setPin('')
      }
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  const pick = (person: SignInName) => {
    if (person.requires_pin) {
      setChosen(person)
      setPin('')
      setProblem(null)
    } else {
      void attempt(person)
    }
  }

  if (names === null) {
    return (
      <main className="shell shell--center">
        <p className="muted">Loading…</p>
      </main>
    )
  }

  // --- PIN step (admins only) -------------------------------------------------
  if (chosen) {
    return (
      <main className="shell shell--center">
        <div className="panel">
          <h1 className="panel__title">{chosen.full_name}</h1>
          <p className="muted">Enter your PIN</p>

          {problem && <p className="banner banner--tight">{problem}</p>}

          <form
            onSubmit={(e) => {
              e.preventDefault()
              if (pin.length >= 4) void attempt(chosen, pin)
            }}
          >
            <input
              className="pin"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              pattern="[0-9]*"
              maxLength={8}
              value={pin}
              autoFocus
              placeholder="••••"
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
            />
            <div className="panel__actions">
              <button
                type="button"
                className="btn btn--quiet"
                onClick={() => {
                  setChosen(null)
                  setPin('')
                  setProblem(null)
                }}
              >
                Back
              </button>
              <button type="submit" className="btn" disabled={busy || pin.length < 4}>
                {busy ? '…' : 'Sign in'}
              </button>
            </div>
          </form>
        </div>
      </main>
    )
  }

  // --- Name list --------------------------------------------------------------
  return (
    <main className="shell">
      <header className="top">
        <div>
          <h1 className="top__hi">Who are you?</h1>
          <p className="top__sub">Tap your name to see your tasks.</p>
        </div>
      </header>

      {problem && <p className="banner">{problem}</p>}

      <div className="names">
        {names.map((person) => (
          <button
            key={person.employee_id}
            type="button"
            className="names__btn"
            disabled={busy}
            onClick={() => pick(person)}
          >
            <span>{person.full_name}</span>
            {person.requires_pin && <span className="names__pin">PIN</span>}
          </button>
        ))}
      </div>

      {names.length === 0 && !problem && (
        <div className="panel">
          <h2 className="panel__title">Nobody is set up yet</h2>
          <p className="muted">Ask Scott to add you.</p>
        </div>
      )}

      <footer className="foot">
        <span className="muted foot__brand">Allied Automotive · Shop Tasks</span>
      </footer>
    </main>
  )
}
